import React from 'react';
import Form from 'react-bootstrap/Form';
import { Field, getFormValues, reduxForm, SubmissionError } from 'redux-form';
import { renderCheckbox, renderSelect } from 'lib/helper/form';
import MASpinner from '../../common/MASpinner';
import InternalUserTypeahead from '../../fields/InternalUserTypeahead';
import { connect } from 'react-redux';
import { addToast } from 'lib/redux/actions/toast';
import { requests } from '../../../lib/helper/agent';
import * as API_ROUTES from '../../../lib/routing/api';
import { invoiceSingleFetch } from '../../../lib/redux/actions/invoices';
import { PAYOR_TYPES } from '../../../lib/helper/invoice';
import { ContactRecordTypeCodes } from '../../../apiTypes';
import { consultantDisplayName } from '../../../lib/helper/formatting';

const mapStateToProps = (state, props) => {
  return {
    currentFormValues: getFormValues('ManualInvoiceNotificationForm')(state),
  };
};

const mapDispatchToProps = {
  addToast,
  invoiceSingleFetch,
};

class ManualInvoiceNotificationForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      manualNotifications: [],
      selectedNotification: null,
      ccUsers: [],
      clearTypeaheads: false,
    };
  }

  componentDidMount() {
    const setNotifications = (manualNotifications) => {
      this.setState({ manualNotifications });
    };
    requests.get(API_ROUTES.MANUAL_INVOICE_NOTIFICATIONS).then((response) => {
      setNotifications(response['hydra:member']);
    });
  }

  onSubmit(values) {
    const { addToast, reset, invoice, invoiceSingleFetch } = this.props;
    const { ccUsers } = this.state;

    const notification = this.state.manualNotifications.find((notif) => Number(notif.id) === Number(values.notification));
    let contactIds = invoice.contactRecords.filter((c) => values['contact-' + c.id]).map((c) => c.id);
    if (!notification) {
      addToast('Please select a notification', false, true);
      return;
    }

    const methods = Object.keys(values)
      .map((k) => (k.includes('method-') ? k.replace('method-', '').toLowerCase() : null))
      .filter((k) => k);

    if (methods.length === 0) {
      throw new SubmissionError({ 'method-Email': 'Please select a method' });
    }

    // If hospital notification, ensure at least one hospital contact is selected
    if (notification.isHospitalNotification) {
      const selectedHospitalContacts = invoice.contactRecords.filter(
        (c) => values['contact-' + c.id] && c.contactRecordType.code === ContactRecordTypeCodes.Hospital,
      );
      if (selectedHospitalContacts.length === 0) {
        throw new SubmissionError({ notification: 'Please select at least one hospital contact' });
      }
    }

    // Get CC emails from selected users
    const ccUserEmails = ccUsers.map((user) => user.email).filter((email) => email);

    const data = {
      invoice_id: Number(invoice.id),
      contact_ids: contactIds,
      consultant: values.consultant,
      consultantSecretary: values.consultantSecretary,
      methods,
      ccEmails: ccUserEmails,
    };

    return requests.patch(API_ROUTES.MANUAL_INVOICE_NOTIFICATIONS + '/' + values.notification + '/send', data).then((response) => {
      addToast('The Invoice was successfully updated', true);
      this.resetForm();
      reset();
      invoiceSingleFetch(Number(invoice.id));
    });
  }

  handleCcUsersChange = (users) => {
    this.setState({ ccUsers: users || [] });
  };

  typeaheadCleared = () => {
    if (this.state.clearTypeaheads) {
      this.setState({ clearTypeaheads: false });
    }
  };

  resetForm = () => {
    this.setState({
      ccUsers: [],
      clearTypeaheads: true,
    });
  };

  componentDidUpdate(prevProps, prevState) {
    const { currentFormValues } = this.props;

    // Only update if the notification selection actually changed
    if (prevProps.currentFormValues?.notification !== currentFormValues?.notification) {
      if (currentFormValues?.notification && Number(currentFormValues.notification) !== Number(this.state.selectedNotification?.id)) {
        this.setState({ selectedNotification: this.state.manualNotifications.find((notif) => Number(notif.id) === Number(currentFormValues.notification)) });
      } else if (!currentFormValues?.notification && this.state.selectedNotification !== null) {
        this.setState({ selectedNotification: null });
      }
    }
  }

  render() {
    const { handleSubmit, isUpdatingInvoice = false, submitting, pristine, invoice, reset, currentFormValues } = this.props;
    const { ccUsers, clearTypeaheads } = this.state;
    const notificationOptions = this.state.manualNotifications
      .filter((mn) => mn.isHospitalNotification === (invoice.payorType.code === PAYOR_TYPES.HOSPITAL))
      .map((manualNotification) => ({ value: manualNotification.id, text: manualNotification.name }));

    const notifMethods = ['Email'];
    // WhatsApp is not available for hospital notifications or when consultant/secretary are selected
    // Consultant/secretary options are only shown for non-patient payor types
    const isPatientPayorType = invoice.payorType.code === PAYOR_TYPES.PATIENT;
    const consultantOrSecretarySelected = !isPatientPayorType && (currentFormValues?.consultant || currentFormValues?.consultantSecretary);
    if (!this.state.selectedNotification || !(this.state.selectedNotification.isHospitalNotification || consultantOrSecretarySelected)) {
      notifMethods.push('WhatsApp');
    }

    const validContacts =
      (this.state.selectedNotification &&
        invoice?.contactRecords &&
        invoice.contactRecords.filter(
          (c) =>
            (c.notifyByEmail || c.notifyByMobile) &&
            ((!this.state.selectedNotification?.isHospitalNotification && c.contactRecordType.code !== ContactRecordTypeCodes.Hospital) ||
              (this.state.selectedNotification?.isHospitalNotification && c.contactRecordType.code === ContactRecordTypeCodes.Hospital)),
        )) ||
      [];

    // Group contacts by type
    const groupedContacts = validContacts.reduce((groups, contact) => {
      const typeName = contact.contactRecordType.name;
      if (!groups[typeName]) {
        groups[typeName] = [];
      }
      groups[typeName].push(contact);
      return groups;
    }, {});

    // Sort group keys to show Patient first, then alphabetically
    const sortedGroupKeys = Object.keys(groupedContacts).sort((a, b) => {
      if (a === 'Patient') return -1;
      if (b === 'Patient') return 1;
      return a.localeCompare(b);
    });

    return (
      <Form onSubmit={handleSubmit(this.onSubmit.bind(this))}>
        <h5>Manually Send Notification</h5>
        <Field
          name="notification"
          label="Notification to send"
          type="select"
          emptyLabel={''}
          options={notificationOptions}
          component={renderSelect}
          helpText={<small>Contacts will be notified by their configured options</small>}
        />
        {this.state.selectedNotification && (
          <div className="mt-3 row">
            <div className="col">
              <p className={'mb-2'}>Recipients</p>
              {sortedGroupKeys.map((typeName) => (
                <div className="mb-3" key={typeName}>
                  <strong className="d-block mb-2">{typeName}</strong>
                  {groupedContacts[typeName].map((c) => (
                    <div className="mb-1" key={c.name + '-' + c.id}>
                      <Field name={'contact-' + c.id} id={c.name + '-' + c.id} label={c.name} component={renderCheckbox} />
                    </div>
                  ))}
                </div>
              ))}
              {invoice.payorType.code !== PAYOR_TYPES.PATIENT && (
                <div className="mb-3">
                  <strong className="d-block mb-2">Consultant</strong>
                  <div className="mb-1" key={'consultant'}>
                    <Field name={'consultant'} id={'consultant'} label={consultantDisplayName(invoice.consultant)} component={renderCheckbox} />
                  </div>
                  <div className="mb-1" key={'consultantSecretary'}>
                    <Field name={'consultantSecretary'} id={'consultantSecretary'} label={'Consultants Secretary'} component={renderCheckbox} />
                  </div>
                </div>
              )}
            </div>

            <div className="col">
              <p className={'mb-2'}>Method</p>
              {notifMethods.map((m) => (
                <div className="mb-1" key={m}>
                  <Field id={m} name={'method-' + m} label={m} component={renderCheckbox} />
                </div>
              ))}
            </div>
          </div>
        )}
        {this.state.selectedNotification && (
          <div className="mt-3">
            <InternalUserTypeahead
              label={<strong>CC Mediaccounts Staff</strong>}
              multiple={true}
              selectedUsers={ccUsers}
              onChange={this.handleCcUsersChange}
              shouldClear={clearTypeaheads}
              hasCleared={this.typeaheadCleared}
              rolesToShow='["ROLE_ADMIN","ROLE_SENIOR_STAFF","ROLE_STAFF","ROLE_CHASER_OUTSOURCER"]'
            />
          </div>
        )}
        {isUpdatingInvoice ? <MASpinner /> : null}
        {!isUpdatingInvoice && !pristine && this.state.selectedNotification ? (
          <>
            <button type="submit" disabled={submitting || pristine} className="btn btn-primary mr-2">
              Send Notification
            </button>
            <button
              type="reset"
              disabled={submitting}
              className="btn btn-secondary"
              onClick={() => {
                this.resetForm();
                reset();
              }}>
              Reset
            </button>
          </>
        ) : null}
      </Form>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'ManualInvoiceNotificationForm',
    enableReinitialize: true,
    destroyOnUnmount: false,
  })(ManualInvoiceNotificationForm),
);
